import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgIf } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { FRESHDESK_HELP_POPUP_STORAGE_PREFIX, FreshdeskHelpModalComponent, FreshdeskHelpPopupPayload } from '@asamco/application-link/shared';
import { first, Subject, takeUntil } from 'rxjs';
import { SelectedCompanyService } from '../../selected-company/selected-company.service';

@Component({
	selector: 'al-help-article-popup-page',
	standalone: true,
	imports: [NgIf, FreshdeskHelpModalComponent],
	templateUrl: './help-article-popup-page.component.html',
	styleUrls: ['./help-article-popup-page.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HelpArticlePopupPageComponent implements OnInit, OnDestroy {
	@ViewChild('helpViewer') helpViewer?: FreshdeskHelpModalComponent;

	loading = true;
	errorMessage = '';
	canRefreshArticles = false;
	private readonly destroy$ = new Subject<void>();

	constructor(
		private readonly route: ActivatedRoute,
		private readonly selectedCompanyService: SelectedCompanyService,
		private readonly titleService: Title,
		private readonly cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		this.route.paramMap.pipe(
			takeUntil(this.destroy$)
		).subscribe(params => {
			this.loading = true;
			this.errorMessage = '';
			this.helpViewer?.close();
			this.cdr.markForCheck();

			const token = params.get('token');
			const payload = token ? this.readPayload(token) : null;
			if (!payload) {
				this.loading = false;
				this.errorMessage = 'The article popup could not be loaded. Open it again from the help window.';
				this.cdr.markForCheck();
				return;
			}

			this.titleService.setTitle(payload.title || 'Help article');
			this.canRefreshArticles = payload.canRefreshArticles;

			this.selectedCompanyService.getCompaniesObservable().pipe(first()).subscribe({
				next: () => {
					this.loading = false;
					this.cdr.markForCheck();
					queueMicrotask(() => {
						this.helpViewer?.open(payload.title, payload.articleIds, payload.origin, payload.selectedArticleId);
						this.cdr.markForCheck();
					});
				},
				error: () => {
					this.loading = false;
					this.errorMessage = 'The article could not be loaded. Please try again.';
					this.cdr.markForCheck();
				},
			});
		});
	}

	private readPayload(token: string): FreshdeskHelpPopupPayload | null {
		const storageKey = `${FRESHDESK_HELP_POPUP_STORAGE_PREFIX}${token}`;

		try {
			const value = localStorage.getItem(storageKey) ?? sessionStorage.getItem(storageKey);
			if (!value) return null;

			sessionStorage.setItem(storageKey, value);
			localStorage.removeItem(storageKey);

			const payload = JSON.parse(value) as FreshdeskHelpPopupPayload;
			if (!Array.isArray(payload.articleIds) || !payload.selectedArticleId) return null;

			return payload;
		} catch {
			return null;
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
